import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../axios-client.js';
import swal from 'sweetalert';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const TYPE_SEATS = { Ride: 17, Train: 11, Yoga: 7, 'Pilates Mat': 7, Barre: 7 };
const CLASS_TYPES = ['Ride', 'Train', 'Yoga', 'Pilates Mat', 'Barre'];
const DEFAULT_DURATION = 45;

// Returns array of 7 Date objects starting from Monday of the selected week
function getWeekDays(mondayDate) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mondayDate);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function toDateStr(date) {
  return date.toISOString().split('T')[0];
}


function getNextMonday() {
  const d = new Date();
  const day = d.getDay();
  const daysUntil = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntil);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function AdminSchedule() {
  const navigate = useNavigate();
  const [monday, setMonday] = useState(() => getNextMonday());
  const [selectedDay, setSelectedDay] = useState(0);
  const [draft, setDraft] = useState({}); // { 'YYYY-MM-DD': [{type, time, duration}] }
  const [submitting, setSubmitting] = useState(false);
  const [sendNotify, setSendNotify] = useState(false);

  // Coaches list
  const [coaches, setCoaches] = useState([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [formType, setFormType] = useState('');
  const [formTime, setFormTime] = useState('');
  const [formDuration, setFormDuration] = useState(DEFAULT_DURATION);
  const [formCoach, setFormCoach] = useState('');
  const [formSpecialClass, setFormSpecialClass] = useState('');

  useEffect(() => {
    axiosClient.get('/perfil').then(({ data }) => {
      if (data.role !== 'admin') navigate('/');
    }).catch(() => navigate('/'));
    axiosClient.get('/coaches-active').then(({ data }) => setCoaches(data.data ?? data)).catch(() => {});
  }, []);

  const weekDays = getWeekDays(monday);

  const prevWeek = () => {
    const d = new Date(monday);
    d.setDate(d.getDate() - 7);
    setMonday(d);
    setSelectedDay(0);
  };

  const nextWeek = () => {
    const d = new Date(monday);
    d.setDate(d.getDate() + 7);
    setMonday(d);
    setSelectedDay(0);
  };

  const selectedDate = toDateStr(weekDays[selectedDay]);
  const dayClasses = draft[selectedDate] || [];
  const totalClasses = Object.values(draft).reduce((sum, arr) => sum + arr.length, 0);

  const openAdd = () => {
    setEditIndex(null);
    setFormType('');
    setFormTime('');
    setFormDuration(DEFAULT_DURATION);
    setFormCoach('');
    setFormSpecialClass('');
    setShowForm(true);
  };

  const openEdit = (idx) => {
    const cls = dayClasses[idx];
    setEditIndex(idx);
    setFormType(cls.type);
    setFormTime(cls.time);
    setFormDuration(cls.duration);
    setFormCoach(cls.coach ?? '');
    setFormSpecialClass(cls.specialClass ?? '');
    setShowForm(true);
  };

  const saveClass = () => {
    if (!formType || !formTime) return;
    const entry = {
      type: formType,
      time: formTime,
      duration: formDuration,
      coach: formCoach || null,
      specialClass: formSpecialClass.trim() || null,
    };
    setDraft((prev) => {
      const existing = prev[selectedDate] ? [...prev[selectedDate]] : [];
      if (editIndex !== null) {
        existing[editIndex] = entry;
      } else {
        existing.push(entry);
        existing.sort((a, b) => a.time.localeCompare(b.time));
      }
      return { ...prev, [selectedDate]: existing };
    });
    setShowForm(false);
  };

  const removeClass = (idx) => {
    setDraft((prev) => {
      const existing = [...(prev[selectedDate] || [])];
      existing.splice(idx, 1);
      const next = { ...prev };
      if (existing.length === 0) delete next[selectedDate];
      else next[selectedDate] = existing;
      return next;
    });
  };

  const handleSubmit = () => {
    if (totalClasses === 0) return;
    swal({
      title: `¿Publicar ${totalClasses} clase${totalClasses !== 1 ? 's' : ''}?`,
      text: 'Se crearán todas las clases y se notificará a los usuarios.',
      icon: 'info',
      buttons: ['Cancelar', 'Publicar'],
    }).then((ok) => {
      if (!ok) return;
      setSubmitting(true);

      const classes = Object.entries(draft).flatMap(([date, arr]) =>
        arr.map((c) => ({
          type_class:    c.type,
          class_date:    date,
          class_time:    c.time,
          duration:      c.duration,
          coach_id:      c.coach ? c.coach.id : null,
          special_class: c.specialClass ?? null,
        }))
      );

      axiosClient.post('/admin/schedule', { classes, notify: sendNotify })
        .then(() => {
          swal('¡Calendario publicado!', 'Las clases fueron creadas y los usuarios fueron notificados.', 'success');
          setDraft({});
        })
        .catch(() => swal('Error', 'No se pudo publicar el calendario.', 'error'))
        .finally(() => setSubmitting(false));
    });
  };

  const formatDate = (date) =>
    date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

  const weekLabel = `${formatDate(weekDays[0])} — ${formatDate(weekDays[6])} ${weekDays[6].getFullYear()}`;

  return (
    <div className="container admin-container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <p className="semi-bold mb-3">Crear horario semanal</p>

      {/* Week navigator + publish button in one row */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-outline-secondary btn-sm" onClick={prevWeek}>‹ Anterior</button>
          <span className="fw-semibold">{weekLabel}</span>
          <button className="btn btn-outline-secondary btn-sm" onClick={nextWeek}>Siguiente ›</button>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="notify-check"
              checked={sendNotify}
              onChange={(e) => setSendNotify(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="notify-check">
              Notificar usuarios
            </label>
          </div>
          <button
            className="btn btn-dark"
            onClick={handleSubmit}
            disabled={totalClasses === 0 || submitting}
          >
            {submitting ? 'Publicando...' : `Publicar semana (${totalClasses} clases)`}
          </button>
        </div>
      </div>

      {/* Day tabs */}
      <div className="schedule-day-tabs mb-4">
        {weekDays.map((date, i) => {
          const dateStr = toDateStr(date);
          const count = draft[dateStr]?.length || 0;
          return (
            <button
              key={i}
              className={`schedule-day-tab ${selectedDay === i ? 'active' : ''}`}
              onClick={() => setSelectedDay(i)}
            >
              <span className="day-name">{DAYS[i]}</span>
              <span className="day-date">{formatDate(date)}</span>
              {count > 0 && <span className="day-badge">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Day classes */}
      <div className="datos-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <p className="semi-bold mb-0">
            {DAYS[selectedDay]} {formatDate(weekDays[selectedDay])}
          </p>
          <button className="btn btn-sm btn-dark" onClick={openAdd}>
            + Agregar clase
          </button>
        </div>

        {dayClasses.length === 0 ? (
          <p className="text-muted" style={{ fontSize: 14 }}>Sin clases para este día. Agrega una.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Hora</th>
                <th>Duración</th>
                <th>Lugares</th>
                <th>Coach</th>
                <th>Clase especial</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dayClasses.map((cls, idx) => (
                <tr key={idx}>
                  <td>
                    <span className={`badge bg-dark`}>
                      {cls.type}
                    </span>
                  </td>
                  <td>{cls.time}</td>
                  <td>{cls.duration} min</td>
                  <td>{TYPE_SEATS[cls.type]}</td>
                  <td>{cls.coach?.name ?? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                  <td>{cls.specialClass ?? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(idx)}>
                      Editar
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeClass(idx)}>
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Week overview */}
      {totalClasses > 0 && (
        <div className="datos-container mt-4">
          <p className="semi-bold mb-3">Resumen de la semana</p>
          <div className="schedule-week-overview">
            {weekDays.map((date, i) => {
              const dateStr = toDateStr(date);
              const classes = draft[dateStr] || [];
              if (classes.length === 0) return null;
              return (
                <div key={i} className="schedule-overview-day">
                  <p className="overview-day-label">{DAYS[i]} {formatDate(date)}</p>
                  {classes.map((cls, idx) => (
                    <div key={idx} className={`overview-class-chip chip-${cls.type === 'Ride' ? 'ride' : cls.type === 'Train' ? 'train' : cls.type === 'Yoga' ? 'yoga' : cls.type === 'Barre' ? 'barre' : 'pilates'}`}>
                      {cls.time} · {cls.type}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add/Edit class modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editIndex !== null ? 'Editar clase' : 'Agregar clase'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-semibold">Tipo</label>
            <select
              className="form-select"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
            >
              <option value="">— Selecciona un tipo —</option>
              {CLASS_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t} ({TYPE_SEATS[t]} lugares)
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Hora</label>
            <input
              type="time"
              className="form-control"
              value={formTime}
              onChange={(e) => setFormTime(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Duración (min)</label>
            <input
              type="number"
              className="form-control"
              min={30}
              max={90}
              value={formDuration}
              onChange={(e) => setFormDuration(Number(e.target.value))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Coach <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(opcional)</span>
            </label>
            <select
              className="form-select"
              value={formCoach ? formCoach.id : ''}
              onChange={(e) => {
                const coach = coaches.find((c) => String(c.id) === e.target.value) ?? null;
                setFormCoach(coach);
              }}
            >
              <option value="">Sin asignar</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-1">
            <label className="form-label fw-semibold">
              Clase especial <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(opcional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="ej. Halloween Ride, Pride Ride…"
              value={formSpecialClass}
              onChange={(e) => setFormSpecialClass(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}
            onClick={() => setShowForm(false)}
          >
            Cancelar
          </Button>
          <Button
            style={{ background: 'var(--light-blue)', border: 'none', fontWeight: 700 }}
            onClick={saveClass}
            disabled={!formType || !formTime}
          >
            {editIndex !== null ? 'Guardar cambios' : 'Agregar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}